<template>
  <section class="event-gallery">
    <div class="container">
      <div class="text-center wow bounceInUp" data-wow-delay="0.1s">
        <span class="event-gallery__eyebrow">{{ $t("latestEvents") }}</span>
        <h2 class="event-gallery__title">{{ $t("eventPageTitle") }}</h2>
      </div>

      <div class="event-gallery__filters wow bounceInUp" data-wow-delay="0.1s">
        <button
          v-for="type in eventTypes"
          :key="type.id"
          type="button"
          class="event-gallery__filter"
          :class="{ 'is-active': selectedType === type.id }"
          @click="filterEvents(type.id)"
        >
          {{ type.name }}
        </button>
      </div>

      <div class="row g-4">
        <div
          v-for="event in filteredEvents"
          :key="event.id"
          class="col-xl-3 col-md-6 wow bounceInUp"
          data-wow-delay="0.1s"
        >
          <article class="event-card">
            <div class="event-card__media" :class="{ 'is-loaded': loadedImages[event.id] }">
              <div v-if="!loadedImages[event.id]" class="event-card__skeleton"></div>
              <img
                :src="getImageUrl(event.image)"
                :alt="event.title"
                class="event-card__image"
                loading="lazy"
                decoding="async"
                @load="markImageLoaded(event.id)"
              />
            </div>
            <div class="event-card__content">
              <h3 class="event-card__title">{{ event.title }}</h3>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import mediaUrl from "../utils/mediaUrl";

export default {
  data() {
    return {
      selectedType: "",
      loadedImages: {},
    };
  },
  computed: {
    ...mapGetters(["events", "event_types"]),
    eventTypes() {
      return [{ id: "", name: this.$t("allEvents") }, ...this.event_types];
    },
    filteredEvents() {
      if (!this.selectedType) {
        return this.events;
      }
      return this.events.filter((event) => event.event_type_id === this.selectedType);
    },
  },
  methods: {
    ...mapActions(["getEvents", "getEventTypes"]),
    filterEvents(typeId) {
      this.selectedType = typeId;
      this.getEvents(typeId || undefined);
    },
    getImageUrl(imageUrl) {
      return mediaUrl(imageUrl);
    },
    markImageLoaded(id) {
      this.$set(this.loadedImages, id, true);
    },
  },
  mounted() {
    this.getEvents();
    this.getEventTypes();
  },
};
</script>

<style scoped>
.event-gallery {
  padding: 88px 0;
  background: #ffffff;
}

.event-gallery__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  margin-bottom: 18px;
  border-radius: 999px;
  background: #f3ecfb;
  color: #4a136c;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.event-gallery__title {
  margin-bottom: 32px;
  font-size: clamp(2rem, 4vw, 3.1rem);
  font-weight: 800;
  color: #351153;
}

.event-gallery__filters {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 36px;
}

.event-gallery__filter {
  padding: 10px 18px;
  border: 1px solid #d8ddea;
  border-radius: 999px;
  background: #ffffff;
  color: #536072;
  font-size: 0.95rem;
  font-weight: 700;
  transition: all 0.2s ease;
}

.event-gallery__filter.is-active,
.event-gallery__filter:hover {
  background: #351153;
  border-color: #351153;
  color: #ffffff;
}

.event-card {
  height: 100%;
  overflow: hidden;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 18px 38px rgba(23, 32, 56, 0.08);
}

.event-card__media {
  position: relative;
  height: 240px;
  background: #eef2f7;
}

.event-card__media.is-loaded .event-card__image {
  opacity: 1;
}

.event-card__skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%);
  background-size: 200% 100%;
  animation: eventShimmer 1.5s infinite;
}

.event-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.event-card__content {
  padding: 20px 22px 24px;
}

.event-card__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.5;
  color: #351153;
}

@media (max-width: 991px) {
  .event-gallery {
    padding: 72px 0;
  }
}

@media (max-width: 576px) {
  .event-gallery {
    padding: 56px 0;
  }

  .event-gallery__title {
    margin-bottom: 24px;
  }

  .event-gallery__filters {
    justify-content: flex-start;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 8px;
    margin-bottom: 24px;
  }

  .event-gallery__filter {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .event-card__media {
    height: 220px;
  }
}

@keyframes eventShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
